import { useMutation } from '@apollo/client';
import React from 'react';

import { useToast } from '@eluve/components';
import { userFragment } from '@eluve/frontend-feature-user-settings';
import { graphql } from '@eluve/graphql.tada';
import { useNamedLogger } from '@eluve/logger';

import { UserRegistrationFlow } from '../flows/UserRegistrationFlow/UserRegistrationFlow';
import {
  getUserProfileData,
  parseUserRegistrationData,
} from '../flows/UserRegistrationFlow/UserRegistrationFlow';
import { useInterstitialFlow } from '../hooks/useInterstitialFlow';
import {
  BaseInterstitial,
  InterstitialContext,
  InterstitialFlowProps,
} from '../types';

// Query for fetching user's registration data (used by UserRegistrationForm)
export const singleUserRegistrationDataQuery = graphql(
  `
    query userRegistrationDataQuery($id: uuid!) {
      users(where: { id: { _eq: $id } }, limit: 1) {
        __typename
        ...User
        specialty
      }
    }
  `,
  [userFragment],
);

export const updateUserProfileMutation = graphql(`
  mutation updateUserProfileSpecialty(
    $userId: uuid!
    $firstName: String!
    $lastName: String!
    $specialty: citext
  ) {
    updateUsersByPk(
      pkColumns: { id: $userId }
      _set: {
        firstName: $firstName
        lastName: $lastName
        specialty: $specialty
      }
    ) {
      id
      __typename
      firstName
      lastName
      specialty
    }
  }
`);

/**
 * Component that handles UserRegistration-specific business logic
 * Enhanced with HOC to get superSkip() and superComplete() methods
 */
const UserRegistrationFlowWithBusinessLogic: React.FC<
  InterstitialFlowProps
> = ({ context }) => {
  const toast = useToast();
  const logger = useNamedLogger('UserRegistrationBusinessLogic');
  const { complete } = useInterstitialFlow('USER_REGISTRATION');

  // Business-specific mutation (user profile update)
  const [updateUserProfileMutationHook] = useMutation(
    updateUserProfileMutation,
    {
      onCompleted: () => {
        logger.info('User profile updated successfully', {
          userId: context.userId,
        });
      },
      onError: (error) => {
        logger.error('Failed to update user profile', {
          userId: context.userId,
          error,
        });
        toast.error('Failed to update user profile. Please try again.');
      },
    },
  );

  const handleComplete = async (data?: unknown) => {
    if (!data) {
      logger.error('Missing user registration data on completion', {
        userId: context.userId,
      });
      toast.error(
        'Unable to complete registration. Please ensure all required fields are filled.',
      );
      return;
    }

    const registrationData = parseUserRegistrationData(data);
    const profileData = getUserProfileData(registrationData);
    await updateUserProfileMutationHook({
      variables: {
        userId: context.userId,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        specialty: profileData.specialty,
      },
    });
    await complete(registrationData);
  };

  return <UserRegistrationFlow context={context} onComplete={handleComplete} />;
};

export const UserRegistrationInterstitial: BaseInterstitial = {
  name: 'USER_REGISTRATION',
  component: UserRegistrationFlowWithBusinessLogic,
  priority: 100, // High priority for user onboarding

  showOnPaths: [/^\/organizations\/[^/]+\/home$/],

  shouldShow: (context: InterstitialContext): boolean =>
    context.userRegistrationShouldShow === true,

  canSkip: (): boolean => {
    // User registration is required and cannot be skipped
    return false;
  },
};
